
import { clamp01 } from "../clamp";
import { legacyToMm, HEADING_STEPS } from "../sim/fixed";
import { EntityKind } from "../net/connection";
import type { NpcMarker_, QuestTarget_ } from "../net/connection";
import type { LandPlot } from "../net/messages";
import { ENTITY_COLOURS } from "../render/entities";
import { elem, installChrome } from "./chrome";
import { markNode } from "./emblem";

export interface MinimapMap {
  /** Which map's labels to draw. See `atlasLabelsFor`. */
  readonly key: string;
  readonly worldWidthMm: number;
  readonly worldHeightMm: number;
  /** `minimap` is optional: four maps ship only a JPEG-XR `minimap.wdp` that
   *  the overview exporter cannot decode, so the manifest has no key for them.
   *  See `OPTIONAL_ASSET_KEYS` in `render/mapassets.ts` -- requiring it here
   *  once cost those maps their entire terrain, not just their overview. */
  readonly assets: { readonly minimap?: string };
}

/** The map's extent, in millimetres -- the two numbers every transform in this
 *  file divides by. Named as its own type because both exported transforms take
 *  it, and a bare `{width, height}` pair at four call sites is two numbers
 *  waiting to be passed in the wrong order. */
export interface MapExtentMm {
  readonly worldWidthMm: number;
  readonly worldHeightMm: number;
}

/** `CHAR_TYPE_NPC` (`length.h:308`), the one marker type drawn as an NPC. */
export const CHAR_TYPE_NPC = 1;

/** One labelled point from the original's own map data. */
export interface AtlasLabel {
  readonly kind: "npc" | "warp";
  readonly xMm: number;
  readonly yMm: number;
  /** The original's own text, cp949-decoded. Never translated -- see the module
   *  doc for why a translated name here would be a fabricated one. */
  readonly name: string;
}

export const A1_ATLAS_LABELS: readonly AtlasLabel[] = [
  { kind: "npc", xMm: legacyToMm(59600), yMm: legacyToMm(55700), name: "무기상점 주인" },
  { kind: "npc", xMm: legacyToMm(59600), yMm: legacyToMm(60500), name: "방어구상점 주인" },
  { kind: "npc", xMm: legacyToMm(67800), yMm: legacyToMm(56500), name: "잡화상점 주인" },
  { kind: "npc", xMm: legacyToMm(67400), yMm: legacyToMm(60700), name: "서평반점 할아버지" },
  { kind: "npc", xMm: legacyToMm(63300), yMm: legacyToMm(62200), name: "노파" },
  { kind: "warp", xMm: legacyToMm(40500), yMm: legacyToMm(7300), name: "자양현" },
  { kind: "npc", xMm: legacyToMm(35400), yMm: legacyToMm(32700), name: "어부" },
  { kind: "npc", xMm: legacyToMm(32500), yMm: legacyToMm(40500), name: "어부" },
  { kind: "npc", xMm: legacyToMm(25100), yMm: legacyToMm(87400), name: "어부" },
  { kind: "npc", xMm: legacyToMm(19500), yMm: legacyToMm(93700), name: "어부" },
  { kind: "npc", xMm: legacyToMm(30400), yMm: legacyToMm(118600), name: "어부" },
  { kind: "npc", xMm: legacyToMm(52500), yMm: legacyToMm(114700), name: "어부" },
  { kind: "npc", xMm: legacyToMm(59100), yMm: legacyToMm(116600), name: "어부" },
  { kind: "npc", xMm: legacyToMm(68000), yMm: legacyToMm(37900), name: "어부" },
  { kind: "npc", xMm: legacyToMm(82100), yMm: legacyToMm(29700), name: "어부" },
  { kind: "npc", xMm: legacyToMm(73100), yMm: legacyToMm(65900), name: "어부" },
  { kind: "warp", xMm: legacyToMm(7600), yMm: legacyToMm(60100), name: "노인" },
];

export const A3_ATLAS_LABELS: readonly AtlasLabel[] = [
  { kind: "npc", xMm: legacyToMm(45000), yMm: legacyToMm(60800), name: "무기상점 주인" },
  { kind: "npc", xMm: legacyToMm(47100), yMm: legacyToMm(60800), name: "방어구상점 주인" },
  { kind: "npc", xMm: legacyToMm(42200), yMm: legacyToMm(60800), name: "잡화상점 주인" },
  { kind: "npc", xMm: legacyToMm(43000), yMm: legacyToMm(57400), name: "서평반점 할아버지" },
  { kind: "npc", xMm: legacyToMm(48700), yMm: legacyToMm(66600), name: "노파" },
  { kind: "warp", xMm: legacyToMm(94800), yMm: legacyToMm(80400), name: "영안읍성" },
  { kind: "warp", xMm: legacyToMm(10000), yMm: legacyToMm(15000), name: "노인" },
];

export const B1_ATLAS_LABELS: readonly AtlasLabel[] = [
  { kind: "npc", xMm: legacyToMm(67600), yMm: legacyToMm(66200), name: "무기상점 주인" },
  { kind: "npc", xMm: legacyToMm(67600), yMm: legacyToMm(61700), name: "방어구상점 주인" },
  { kind: "npc", xMm: legacyToMm(59000), yMm: legacyToMm(68900), name: "잡화상점 주인" },
  { kind: "npc", xMm: legacyToMm(60900), yMm: legacyToMm(59600), name: "서평반점 할아버지" },
  { kind: "npc", xMm: legacyToMm(58800), yMm: legacyToMm(63300), name: "노파" },
  { kind: "warp", xMm: legacyToMm(87600), yMm: legacyToMm(112700), name: "복정현" },
  { kind: "npc", xMm: legacyToMm(67500), yMm: legacyToMm(53900), name: "어부" },
  { kind: "npc", xMm: legacyToMm(74200), yMm: legacyToMm(87900), name: "어부" },
  { kind: "npc", xMm: legacyToMm(20600), yMm: legacyToMm(55200), name: "어부" },
  { kind: "npc", xMm: legacyToMm(21800), yMm: legacyToMm(67900), name: "어부" },
  { kind: "npc", xMm: legacyToMm(22100), yMm: legacyToMm(86500), name: "어부" },
  { kind: "npc", xMm: legacyToMm(23100), yMm: legacyToMm(97600), name: "어부" },
  { kind: "npc", xMm: legacyToMm(23400), yMm: legacyToMm(107700), name: "어부" },
  { kind: "warp", xMm: legacyToMm(90800), yMm: legacyToMm(8700), name: "노인" },
];

export const B3_ATLAS_LABELS: readonly AtlasLabel[] = [
  { kind: "npc", xMm: legacyToMm(44800), yMm: legacyToMm(38700), name: "무기상점 주인" },
  { kind: "npc", xMm: legacyToMm(46100), yMm: legacyToMm(37400), name: "방어구상점 주인" },
  { kind: "npc", xMm: legacyToMm(38900), yMm: legacyToMm(35600), name: "잡화상점 주인" },
  { kind: "npc", xMm: legacyToMm(47100), yMm: legacyToMm(34700), name: "서평반점 할아버지" },
  { kind: "npc", xMm: legacyToMm(41900), yMm: legacyToMm(30900), name: "노파" },
  { kind: "warp", xMm: legacyToMm(10600), yMm: legacyToMm(8800), name: "조안읍성" },
  { kind: "warp", xMm: legacyToMm(77200), yMm: legacyToMm(14000), name: "노인" },
];

export const C1_ATLAS_LABELS: readonly AtlasLabel[] = [
  { kind: "npc", xMm: legacyToMm(43000), yMm: legacyToMm(60800), name: "무기상점 주인" },
  { kind: "npc", xMm: legacyToMm(40300), yMm: legacyToMm(58500), name: "방어구상점 주인" },
  { kind: "npc", xMm: legacyToMm(38300), yMm: legacyToMm(69300), name: "잡화상점 주인" },
  { kind: "npc", xMm: legacyToMm(31500), yMm: legacyToMm(56000), name: "서평반점 할아버지" },
  { kind: "npc", xMm: legacyToMm(41700), yMm: legacyToMm(67100), name: "노파" },
  { kind: "warp", xMm: legacyToMm(13700), yMm: legacyToMm(12600), name: "박라현" },
  { kind: "npc", xMm: legacyToMm(71100), yMm: legacyToMm(23400), name: "어부" },
  { kind: "npc", xMm: legacyToMm(73200), yMm: legacyToMm(39000), name: "어부" },
  { kind: "npc", xMm: legacyToMm(77200), yMm: legacyToMm(47200), name: "어부" },
  { kind: "npc", xMm: legacyToMm(76300), yMm: legacyToMm(80900), name: "어부" },
  { kind: "npc", xMm: legacyToMm(77300), yMm: legacyToMm(90500), name: "어부" },
  { kind: "npc", xMm: legacyToMm(42800), yMm: legacyToMm(48000), name: "어부" },
  { kind: "npc", xMm: legacyToMm(38600), yMm: legacyToMm(84900), name: "어부" },
  { kind: "npc", xMm: legacyToMm(81900), yMm: legacyToMm(31500), name: "어부" },
  { kind: "warp", xMm: legacyToMm(12500), yMm: legacyToMm(111800), name: "노인" },
];

export const C3_ATLAS_LABELS: readonly AtlasLabel[] = [
  { kind: "npc", xMm: legacyToMm(46000), yMm: legacyToMm(46700), name: "무기상점 주인" },
  { kind: "npc", xMm: legacyToMm(43500), yMm: legacyToMm(46700), name: "방어구상점 주인" },
  { kind: "npc", xMm: legacyToMm(48400), yMm: legacyToMm(38700), name: "잡화상점 주인" },
  { kind: "npc", xMm: legacyToMm(39400), yMm: legacyToMm(39000), name: "서평반점 할아버지" },
  { kind: "npc", xMm: legacyToMm(43300), yMm: legacyToMm(37600), name: "노파" },
  { kind: "warp", xMm: legacyToMm(90100), yMm: legacyToMm(15100), name: "평무읍성" },
  { kind: "warp", xMm: legacyToMm(13600), yMm: legacyToMm(89900), name: "노인" },
];

export const N_DESERT_ATLAS_LABELS: readonly AtlasLabel[] = [
  { kind: "warp", xMm: legacyToMm(10900), yMm: legacyToMm(142700), name: "자양현" },
  { kind: "warp", xMm: legacyToMm(139300), yMm: legacyToMm(13500), name: "복정현" },
  { kind: "warp", xMm: legacyToMm(14900), yMm: legacyToMm(13500), name: "박라현" },
];

export const N_FLAME_ATLAS_LABELS: readonly AtlasLabel[] = [
  { kind: "warp", xMm: legacyToMm(10300), yMm: legacyToMm(144600), name: "자양현" },
  { kind: "warp", xMm: legacyToMm(7600), yMm: legacyToMm(6000), name: "복정현" },
  { kind: "warp", xMm: legacyToMm(145800), yMm: legacyToMm(75100), name: "박라현" },
];

export const N_SNOW_ATLAS_LABELS: readonly AtlasLabel[] = [
  { kind: "warp", xMm: legacyToMm(139600), yMm: legacyToMm(15200), name: "자양현" },
  { kind: "warp", xMm: legacyToMm(15500), yMm: legacyToMm(28700), name: "복정현" },
  { kind: "warp", xMm: legacyToMm(75200), yMm: legacyToMm(141800), name: "박라현" },
];

const ATLAS_LABELS_BY_MAP: Readonly<Record<string, readonly AtlasLabel[]>> = {
  a1: A1_ATLAS_LABELS,
  a3: A3_ATLAS_LABELS,
  b1: B1_ATLAS_LABELS,
  b3: B3_ATLAS_LABELS,
  c1: C1_ATLAS_LABELS,
  c3: C3_ATLAS_LABELS,
  n_desert_01: N_DESERT_ATLAS_LABELS,
  n_flame_01: N_FLAME_ATLAS_LABELS,
  n_snowm_01: N_SNOW_ATLAS_LABELS,
};

export function atlasLabelsFor(mapKey: string): readonly AtlasLabel[] {
  return ATLAS_LABELS_BY_MAP[mapKey] ?? [];
}

export interface MapFraction {
  readonly u: number;
  readonly v: number;
}

export function worldToFraction(xMm: number, yMm: number, extent: MapExtentMm): MapFraction {
  const u = clamp01(xMm / extent.worldWidthMm);
  const v = clamp01(1 - yMm / extent.worldHeightMm);
  return { u, v };
}

/** A fraction, at a given canvas size. */
export function fractionToPixel(
  fraction: MapFraction,
  widthPx: number,
  heightPx: number,
): { readonly x: number; readonly y: number } {
  return { x: fraction.u * widthPx, y: fraction.v * heightPx };
}

export function radarOffset(
  xMm: number,
  yMm: number,
  playerXMm: number,
  playerYMm: number,
  pxPerMm: number,
): { readonly dx: number; readonly dy: number } {
  return {
    dx: (xMm - playerXMm) * pxPerMm,
    dy: -(yMm - playerYMm) * pxPerMm,
  };
}

/** Keep a quest waypoint visible at the radar rim when it is out of range. */
export function radarQuestTargetMarker(
  xMm: number,
  yMm: number,
  playerXMm: number,
  playerYMm: number,
  pxPerMm: number,
  radiusPx: number,
): { readonly x: number; readonly y: number; readonly angle: number; readonly inside: boolean } {
  const { dx, dy } = radarOffset(xMm, yMm, playerXMm, playerYMm, pxPerMm);
  const distance = Math.hypot(dx, dy);
  const inside = distance <= radiusPx;
  if (distance === 0) return { x: radiusPx, y: radiusPx, angle: 0, inside: true };
  const scale = inside ? 1 : (radiusPx - 9) / distance;
  return { x: radiusPx + dx * scale, y: radiusPx + dy * scale, angle: Math.atan2(dy, dx), inside };
}

export function groundDrawRect(
  playerXMm: number,
  playerYMm: number,
  pxPerMm: number,
  imgWidthPx: number,
  imgHeightPx: number,
  centreX: number,
  centreY: number,
  extent: MapExtentMm,
): { readonly x: number; readonly y: number; readonly width: number; readonly height: number } {
  const scaleX = (extent.worldWidthMm / imgWidthPx) * pxPerMm;
  const scaleY = (extent.worldHeightMm / imgHeightPx) * pxPerMm;
  const fraction = worldToFraction(playerXMm, playerYMm, extent);
  const playerImgX = fraction.u * imgWidthPx;
  const playerImgY = fraction.v * imgHeightPx;
  return {
    x: centreX - playerImgX * scaleX,
    y: centreY - playerImgY * scaleY,
    width: imgWidthPx * scaleX,
    height: imgHeightPx * scaleY,
  };
}

export function headingToScreenAngle(heading: number): number {
  return -(heading / HEADING_STEPS) * Math.PI * 2;
}

/** One nearby mover, flattened from `RemoteEntity` the way the HUD flattens
 *  its own `TargetView` -- so this panel cannot accidentally grow a dependency
 *  on a field it never draws. */
export interface MinimapEntity {
  readonly id: number;
  readonly kind: number;
  readonly xMm: number;
  readonly yMm: number;
}

export interface MinimapModel {
  readonly xMm: number;
  readonly yMm: number;
  readonly heading: number;
  readonly entities: readonly MinimapEntity[];
  /** Facts the server owns and this panel only reports. */
  readonly observerCount?: number;
}

/** A quest waypoint owned by the server and drawn on both minimap views. */
export interface MinimapQuestTarget {
  readonly xMm: number;
  readonly yMm: number;
  readonly description: string;
}

/** A map point under the pointer, or the nearest server-provided label. */
export interface MinimapInfo {
  readonly xMm: number;
  readonly yMm: number;
  readonly label: AtlasLabel | null;
}

/** Convert the north-up image fraction back to map-local millimetres. */
export function fractionToWorld(
  u: number,
  v: number,
  extent: MapExtentMm,
): { readonly xMm: number; readonly yMm: number } {
  return {
    xMm: clamp01(u) * extent.worldWidthMm,
    yMm: (1 - clamp01(v)) * extent.worldHeightMm,
  };
}

/** Find the label a pointer is actually over, without making the canvas own a
 * second copy of atlas coordinate arithmetic. */
export function infoAtFraction(
  u: number,
  v: number,
  extent: MapExtentMm,
  labels: readonly AtlasLabel[],
  hitRadius = 0.025,
): MinimapInfo {
  const fraction = { u: clamp01(u), v: clamp01(v) };
  let label: AtlasLabel | null = null;
  let nearest = hitRadius * hitRadius;
  for (const candidate of labels) {
    const point = worldToFraction(candidate.xMm, candidate.yMm, extent);
    const distance = (point.u - fraction.u) ** 2 + (point.v - fraction.v) ** 2;
    if (distance <= nearest) {
      nearest = distance;
      label = candidate;
    }
  }
  const world = fractionToWorld(fraction.u, fraction.v, extent);
  return { ...world, label };
}

/** `GUILD_EMPTY_AREA` (`locale_game.txt:172`), what `AtlasWindow.OnUpdate`
 *  shows for `"empty_guild_area"` (`uiminimap.py:142-143`). */
export const GUILD_EMPTY_AREA = "Empty land";

/** One plot as `CPythonMiniMap::RegisterGuildArea` keeps it, in legacy units
 *  (`PythonMiniMap.cpp:733-742`). */
export interface GuildAreaOwner {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly guildId: number;
}

/** The guild plot under an atlas point, as the last arm of `GetAtlasInfo`
 *  reads it (`PythonMiniMap.cpp:1191-1210`): the first plot whose rectangle
 *  holds the point, named by its guild when that name is known and by
 *  `GUILD_EMPTY_AREA` otherwise, placed at the plot's integer centre. */
export function guildAreaInfoAt(
  u: number,
  v: number,
  extent: MapExtentMm,
  areas: readonly GuildAreaOwner[],
  names: ReadonlyMap<number, string>,
): { readonly name: string; readonly guildId: number; readonly xMm: number; readonly yMm: number } | null {
  const world = fractionToWorld(u, v, extent);
  for (const area of areas) {
    if (world.xMm < legacyToMm(area.x) || world.yMm < legacyToMm(area.y)) continue;
    if (world.xMm > legacyToMm(area.x + area.width) || world.yMm > legacyToMm(area.y + area.height)) continue;
    const name = names.get(area.guildId);
    return {
      name: name ?? GUILD_EMPTY_AREA,
      guildId: name === undefined ? 0 : area.guildId,
      xMm: legacyToMm(area.x + Math.trunc(area.width / 2)),
      yMm: legacyToMm(area.y + Math.trunc(area.height / 2)),
    };
  }
  return null;
}

/** The original panel displays seconds as a compact D/H/M/S duration. */
export function formatDuration(seconds: number): string {
  let remaining = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const days = Math.floor(remaining / 86_400);
  remaining %= 86_400;
  const hours = Math.floor(remaining / 3_600);
  remaining %= 3_600;
  const minutes = Math.floor(remaining / 60);
  const secs = remaining % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

/** The native minimap starts at 2x and each button moves one rung of its
 * exact .5 / 1 / 2 / 4 ladder. `CPythonMiniMap::__Initialize` supplies the
 * start, while `ScaleUp` and `ScaleDown` supply both the multipliers and the
 * end stops (`PythonMiniMap.cpp:494-506,1428-1430`). */
export const MINIMAP_INITIAL_SCALE = 2;
export const MINIMAP_MIN_SCALE = 0.5;
export const MINIMAP_MAX_SCALE = 4;

/** `ServerInfo`: `__SetServerInfo("%s, %s " % (serverName, channelName))`,
 *  stripped (`intrologin.py:695-697,1290`), for this build's one server and
 *  one channel, `SERVER_NAME` and `CH1_NAME` (`serverinfo.py:1,3`). */
export const LEGACY_MINIMAP_SERVER_INFO = "Metin2, CH1";

export function legacyMinimapPositionLabel(xMm: number, yMm: number): string {
  return `(${Math.round(xMm / 1000).toFixed(0)}, ${Math.round(yMm / 1000).toFixed(0)})`;
}

/** English legacy `MINIMAP_OBSERVER_COUNT` text (`locale_game.txt:301`). */
export function legacyMinimapObserverLabel(count: number): string {
  return `${count} observers`;
}

/** The native `ScaleUp`: double the world under the fixed-size radar, then
 * stop at 4x. */
export function nextMinimapScaleUp(scale: number): number {
  return Math.min(MINIMAP_MAX_SCALE, scale * 2);
}

/** The native `ScaleDown`: halve the world under the fixed-size radar, then
 * stop at .5x. */
export function nextMinimapScaleDown(scale: number): number {
  return Math.max(MINIMAP_MIN_SCALE, scale * 0.5);
}

const RADAR_RADIUS_MM = 180_000; // the radar's 180 m reach (uiminimap.py:108, the 136 px window over minimap.sub)

/** Pixels per world millimetre at one native scale unit. At the original 2x
 * default this is exactly the old browser radius, so restoring the controls
 * does not change how the untouched minimap reads. */
export function radarPixelsPerMm(sizePx: number, scale: number): number {
  return ((sizePx / 2) / RADAR_RADIUS_MM) * (scale / MINIMAP_INITIAL_SCALE);
}

/** The original `CPythonMiniMap::__Initialize` sets a 128x128 radar
 * (`PythonMiniMap.cpp:1438`, `SetMiniMapSize(128.0f, 128.0f)`). This one value
 * drives the backing canvas, responsive display box, and control offsets. */
export const RADAR_SIZE_PX = 128;

export const MINIMAP_BOARD_FILL = "rgba(26, 21, 16, 0.995)";

/** `uiscript/minimap.py` MiniMap / OpenWindow size (`width`/`height`: 136/137). */
export const MINIMAP_WINDOW_WIDTH = 136;
export const MINIMAP_WINDOW_HEIGHT = 137; // minimap.sub is 136x137 (uiminimap.py:172-177)

/** Radar inset inside OpenWindow (`MiniMapWindow` at x=4, y=5). */
export const MINIMAP_RADAR_OFFSET_X = 4;
export const MINIMAP_RADAR_OFFSET_Y = 5;

/** Control positions from `uiscript/minimap.py` (OpenWindow / CloseWindow). */
export const MINIMAP_LAYOUT = {
  scaleUp: { x: 101, y: 116, w: 15, h: 15 },
  scaleDown: { x: 115, y: 103, w: 15, h: 15 },
  hide: { x: 111, y: 6, w: 19, h: 20 },
  atlasOpen: { x: 12, y: 12, w: 15, h: 15 },
  show: { x: 100, y: 4, w: 32, h: 32 },
  closeWindow: { w: 132, h: 48 },
  /** Combined status line sits where ServerInfo/PositionInfo begin (y=140). */
  infoY: 140,
} as const;

export function atlasPixelSize(extent: MapExtentMm): { readonly width: number; readonly height: number } {
  return {
    width: Math.max(1, Math.round(extent.worldWidthMm / 2000)),
    height: Math.max(1, Math.round(extent.worldHeightMm / 2000)),
  };
}

/** `uiscript/minimap.py`'s `ROOT`, imported under the name that file spells. */
const MINIMAP_ART = "/ui/textures/minimap";

/** `GuildArea01.sub`, the flag the atlas draws on every guild plot
 *  (`PythonMiniMap.cpp:570`). */
export const GUILD_AREA_FLAG = `${MINIMAP_ART}/guildarea01.png`;

/** One plot as `TGuildAreaInfo` keeps it (`PythonMiniMap.h:128-136`), in
 *  map-local millimetres. */
export interface GuildAreaMm {
  readonly xMm: number;
  readonly yMm: number;
  readonly widthMm: number;
  readonly heightMm: number;
}

/** Where the flag's top-left corner goes on an atlas buffer: the plot's
 *  projected centre less half the flag's integer size
 *  (`PythonMiniMap.cpp:952-953,1043-1044`). */
export function guildAreaFlagPosition(
  area: GuildAreaMm,
  extent: MapExtentMm,
  widthPx: number,
  heightPx: number,
  flagWidthPx: number,
  flagHeightPx: number,
): { readonly x: number; readonly y: number } {
  const centre = fractionToPixel(
    worldToFraction(area.xMm + area.widthMm / 2, area.yMm + area.heightMm / 2, extent),
    widthPx,
    heightPx,
  );
  return {
    x: centre.x - Math.trunc(flagWidthPx / 2), // PythonMiniMap.cpp:1043
    y: centre.y - Math.trunc(flagHeightPx / 2), // PythonMiniMap.cpp:1044
  };
}

/** `CPythonMiniMap`'s half of a land list: every plot, owned or vacant. */
export interface MiniMapGuildAreas {
  clearGuildArea(): void;
  registerGuildArea(id: number, guildId: number, x: number, y: number, width: number, height: number): void;
}

/** `CPythonBackground`'s half: the rectangles the player's own guild may build on. */
export interface BackgroundGuildAreas {
  clearGuildArea(): void;
  registerGuildArea(isx: number, isy: number, iex: number, iey: number): void;
}

/** `CPythonNetworkStream::RecvLandPacket`
 *  (`PythonNetworkStreamPhaseGame.cpp:4176-4221`): clear both lists, register
 *  every plot on the minimap and the own guild's plots as buildable
 *  rectangles, then ask for the symbols of every guild that owns one. An
 *  `ownGuildId` of zero owns no plot. */
export function recvLandList(
  lands: readonly LandPlot[],
  ownGuildId: number,
  miniMap: MiniMapGuildAreas,
  background: BackgroundGuildAreas,
  downloadSymbol: (guildIds: number[]) => void,
): void {
  const owners: number[] = [];
  miniMap.clearGuildArea(); // PythonNetworkStreamPhaseGame.cpp:4188
  background.clearGuildArea(); // PythonNetworkStreamPhaseGame.cpp:4189
  for (const land of lands) {
    miniMap.registerGuildArea(land.id, land.guild_id, land.x, land.y, land.width, land.height); // PythonNetworkStreamPhaseGame.cpp:4198-4203
    if (ownGuildId !== 0 && land.guild_id === ownGuildId) { // PythonNetworkStreamPhaseGame.cpp:4205-4206
      background.registerGuildArea(land.x, land.y, land.x + land.width, land.y + land.height); // PythonNetworkStreamPhaseGame.cpp:4208-4211
    }
    if (land.guild_id !== 0) owners.push(land.guild_id); // PythonNetworkStreamPhaseGame.cpp:4214-4215
  }
  downloadSymbol(owners); // PythonNetworkStreamPhaseGame.cpp:4218
}

const STYLE_ID = "minimap-styles";

const ENTITY_DOT_COLOUR: Record<number, string> = {
  [EntityKind.PLAYER]: ENTITY_COLOURS.other,
  [EntityKind.MONSTER]: ENTITY_COLOURS.monster,
  [EntityKind.NPC]: ENTITY_COLOURS.npc,
  [EntityKind.STONE]: ENTITY_COLOURS.stone,
  [EntityKind.ITEM]: ENTITY_COLOURS.loot,
};

export class Minimap {
  private readonly map: MinimapMap;
  private labels: readonly AtlasLabel[];
  private readonly root: HTMLElement;
  private readonly openWindow: HTMLElement;
  private readonly closeWindow: HTMLElement;
  private readonly radarCanvas: HTMLCanvasElement;
  private readonly radarCtx: CanvasRenderingContext2D;
  private readonly atlasRoot: HTMLElement;
  private readonly atlasPanel: HTMLElement;
  private readonly atlasClose: HTMLButtonElement;
  private readonly atlasCanvas: HTMLCanvasElement;
  private readonly atlasCtx: CanvasRenderingContext2D;
  private readonly atlasTooltip: HTMLElement;
  private readonly guildAreaFlag: HTMLImageElement;
  private readonly guildAreas: GuildAreaMm[] = [];
  private readonly guildOwners: GuildAreaOwner[] = [];
  private guildNames: ReadonlyMap<number, string> = new Map();
  private guildEmblems: ReadonlyMap<number, readonly number[]> = new Map();
  private wantEmblems: (guildIds: number[]) => void = () => {};
  private readonly infoRoot: HTMLElement;
  private readonly showButton: HTMLButtonElement;
  private readonly hideButton: HTMLButtonElement;
  private readonly atlasOpenButton: HTMLButtonElement;
  private readonly scaleDownButton: HTMLButtonElement;
  private readonly scaleUpButton: HTMLButtonElement;
  private readonly dpr: number;
  private overview: ImageBitmap | null = null;
  private open = false;
  private visible = true;
  private scale = MINIMAP_INITIAL_SCALE;
  private imprisonmentSeconds = 0;
  private imprisonmentUntilMs = 0;
  private readonly questTargets = new Map<number, MinimapQuestTarget>();
  private last: MinimapModel = { xMm: 0, yMm: 0, heading: 0, entities: [] };

  constructor(container: HTMLElement, map: MinimapMap) {
    installChrome(container.ownerDocument);
    ensureStyles();
    this.map = map;
    // The shipped list, until the server sends its own -- see `setNpcMarkers`.
    this.labels = atlasLabelsFor(map.key);
    this.dpr = Math.min(2, globalThis.devicePixelRatio ?? 1);

    // `uiscript/minimap.py`: MiniMap is 136x137 at (SCREEN_WIDTH-136, 0) with
    // OpenWindow / CloseWindow children. Frame art is `minimap.sub` (not under
    // /ui yet); layout matches the script coordinates without inventing pixels.
    this.root = elem(document, "div", "minimap");
    this.root.style.width = `${MINIMAP_WINDOW_WIDTH}px`;
    this.root.style.height = `${MINIMAP_WINDOW_HEIGHT}px`;
    container.append(this.root);

    this.openWindow = elem(document, "div", "minimap-open");
    this.root.append(this.openWindow);

    this.radarCanvas = elem(document, "canvas", "minimap-radar");
    this.radarCanvas.width = RADAR_SIZE_PX * this.dpr;
    this.radarCanvas.height = RADAR_SIZE_PX * this.dpr;
    const radarCtx = this.radarCanvas.getContext("2d");
    if (radarCtx === null) throw new Error("2d context unavailable for minimap radar");
    this.radarCtx = radarCtx;
    this.openWindow.append(this.radarCanvas);

    const controls = elem(document, "div", "minimap-controls");
    // Legacy locale names these controls “Zoom out” and “Zoom in”
    // (`uiminimap.py:221-224`; `locale_game.txt:299-300`). Atlas open uses
    // `MINIMAP_SHOW_AREAMAP` (`locale_game.txt:302` -- "Open Large Map").
    this.scaleDownButton = controlButton("−", "Zoom out");
    this.scaleUpButton = controlButton("+", "Zoom in");
    this.hideButton = controlButton("×", "Hide minimap");
    this.atlasOpenButton = controlButton("▣", "Open Large Map");
    placeControl(this.scaleDownButton, MINIMAP_LAYOUT.scaleDown, "minimap-scale-down");
    placeControl(this.scaleUpButton, MINIMAP_LAYOUT.scaleUp, "minimap-scale-up");
    placeControl(this.hideButton, MINIMAP_LAYOUT.hide, "minimap-hide");
    placeControl(this.atlasOpenButton, MINIMAP_LAYOUT.atlasOpen, "minimap-atlas-open");
    this.scaleDownButton.addEventListener("click", () => this.scaleDown());
    this.scaleUpButton.addEventListener("click", () => this.scaleUp());
    this.hideButton.addEventListener("click", () => this.hideMiniMap());
    // Native AtlasShowButton calls ShowAtlas (`uiminimap.py:351,477-481`), open-only.
    this.atlasOpenButton.addEventListener("click", () => this.setOpen(true));
    controls.append(
      this.scaleDownButton,
      this.scaleUpButton,
      this.hideButton,
      this.atlasOpenButton,
    );
    this.openWindow.append(controls);

    this.infoRoot = elem(document, "div", "minimap-info");
    this.infoRoot.setAttribute("aria-live", "polite");
    this.openWindow.append(this.infoRoot);

    this.closeWindow = elem(document, "div", "minimap-close");
    this.closeWindow.hidden = true;
    this.showButton = controlButton("M", "Show minimap");
    placeControl(this.showButton, MINIMAP_LAYOUT.show, "minimap-show");
    this.showButton.addEventListener("click", () => this.showMiniMap());
    this.closeWindow.append(this.showButton);
    this.root.append(this.closeWindow);

    container.ownerDocument.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.open) return;
      event.preventDefault();
      event.stopPropagation();
      this.setOpen(false);
    });

    this.atlasRoot = elem(document, "div", "atlas-backdrop");
    this.atlasRoot.hidden = true;

    this.atlasPanel = elem(document, "div", "atlas-panel");
    this.atlasRoot.append(this.atlasPanel);

    const header = elem(document, "div", "atlas-header");
    this.atlasClose = controlButton("×", "Close atlas");
    this.atlasClose.className = "atlas-close";
    this.atlasClose.addEventListener("click", () => this.setOpen(false));
    header.append(this.atlasClose);
    this.atlasPanel.append(header);

    this.atlasCanvas = elem(document, "canvas", "atlas-canvas");
    const atlasPx = atlasPixelSize(map);
    this.atlasCanvas.width = atlasPx.width;
    this.atlasCanvas.height = atlasPx.height;
    this.atlasCanvas.style.aspectRatio = `${atlasPx.width} / ${atlasPx.height}`;
    this.atlasCanvas.style.width = `min(300px, 30vw, calc(46vh * ${atlasPx.width / atlasPx.height}))`;
    const atlasCtx = this.atlasCanvas.getContext("2d");
    if (atlasCtx === null) throw new Error("2d context unavailable for minimap atlas");
    this.atlasCtx = atlasCtx;
    this.atlasPanel.append(this.atlasCanvas);
    this.atlasTooltip = elem(document, "div", "atlas-tooltip");
    this.atlasTooltip.hidden = true;
    this.atlasTooltip.setAttribute("role", "tooltip");
    this.atlasPanel.append(this.atlasTooltip);
    // The flag's picture, held by an element so the atlas canvas can draw it.
    this.guildAreaFlag = elem(document, "img", "minimap-guild-area-flag");
    this.guildAreaFlag.hidden = true;
    this.guildAreaFlag.alt = "";
    this.guildAreaFlag.addEventListener("load", () => {
      if (this.open) this.paintAtlas();
    });
    this.guildAreaFlag.src = GUILD_AREA_FLAG;
    this.atlasPanel.append(this.guildAreaFlag);
    this.atlasCanvas.addEventListener("pointermove", (event) => this.showAtlasInfo(event));
    this.atlasCanvas.addEventListener("pointerleave", () => {
      this.atlasTooltip.hidden = true;
    });
    container.append(this.atlasRoot);

    void this.loadOverview();
    this.paintRadar();
  }

  /** Allowed to fail: without it both canvases still draw the player, the
   *  heading and every nearby entity against a flat tint, which is a loss of
   *  scenery and not of function -- the same bar `main.ts` holds the creature
   *  palette fetch to. */
  private async loadOverview(): Promise<void> {
    try {
      // Four maps have no overview at all -- see `OPTIONAL_ASSET_KEYS` in
      // `render/mapassets.ts`. Absence is now expressed by the URL simply not
      // being there, so it is checked here rather than reaching `fetch` as
      // `undefined` and failing on a request for the string "undefined".
      const url = this.map.assets.minimap;
      if (url === undefined || url === "") {
        console.warn("this map ships no minimap overview; drawing the radar on a flat tint");
        return;
      }
      const response = await fetch(url);
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
      const blob = await response.blob();
      this.overview = await createImageBitmap(blob);
      this.paintRadar();
      if (this.open) this.paintAtlas();
    } catch (error) {
      console.warn(`minimap overview unavailable (${String(error)})`);
    }
  }

  /** Redraws the radar every call and the atlas only while it is open --
   *  matching `BagPanel`'s own reason for skipping work behind `hidden`, a
   *  panel nobody can see has nothing to gain from being repainted sixty
   *  times a second. */
  update(model: MinimapModel): void {
    this.last = model;
    this.updateObserverCount(model.observerCount ?? 0);
    this.paintRadar();
    if (this.open) this.paintAtlas();
    this.paintServerInfo();
  }

  updateQuestTarget(event: QuestTarget_): void {
    if (event.kind === "create") {
      this.questTargets.set(event.id, {
        xMm: event.xMm,
        yMm: event.yMm,
        description: event.description,
      });
      // Both native create handlers open the atlas after installing the
      // server target (`PythonNetworkStreamPhaseGame.cpp:4239,4268`).  Set the
      // state explicitly: a second target must not toggle an open atlas shut.
      this.setOpen(true);
    } else if (event.kind === "update") {
      const target = this.questTargets.get(event.id);
      if (target !== undefined) {
        this.questTargets.set(event.id, { ...target, xMm: event.xMm, yMm: event.yMm });
      }
    } else {
      this.questTargets.delete(event.id);
    }
    this.infoRoot.dataset.targetCount = String(this.questTargets.size);
    this.paintRadar();
    if (this.open) this.paintAtlas();
  }

  /** `CPythonMiniMap::ClearGuildArea` (`PythonMiniMap.cpp:728-731`). */
  clearGuildArea(): void {
    this.guildAreas.length = 0;
    this.guildOwners.length = 0;
    if (this.open) this.paintAtlas();
  }

  /** `CPythonMiniMap::RegisterGuildArea` (`PythonMiniMap.cpp:733-742`), in the
   *  plot's own legacy units. */
  registerGuildArea(_id: number, guildId: number, x: number, y: number, width: number, height: number): void {
    this.guildOwners.push({ x, y, width, height, guildId });
    this.guildAreas.push({
      xMm: legacyToMm(x),
      yMm: legacyToMm(y),
      widthMm: legacyToMm(width),
      heightMm: legacyToMm(height),
    });
    if (this.open) this.paintAtlas();
  }

  /** The guild names the atlas reads a plot's owner from
   *  (`CPythonGuild::GetGuildName`, `PythonMiniMap.cpp:1200`). */
  setGuildNames(names: ReadonlyMap<number, string>): void {
    this.guildNames = names;
  }

  /** The served guild signs the tooltip's `MarkBox` draws beside an owner's
   *  name (`uiminimap.py:156-160`), and how to ask for one not yet served. */
  setGuildEmblems(emblems: ReadonlyMap<number, readonly number[]>, want: (guildIds: number[]) => void): void {
    this.guildEmblems = emblems;
    this.wantEmblems = want;
  }

  /** Opens or closes the atlas, returning the new state -- the same shape as
   *  `BagPanel.toggle`, for a caller that wants to drive both from one
   *  keyboard handler without special-casing either. */
  toggleAtlas(): boolean {
    this.setOpen(!this.open);
    return this.open;
  }

  private setOpen(open: boolean): void {
    this.open = open;
    this.atlasRoot.hidden = !open;
    if (open) this.paintAtlas();
  }

  get atlasOpen(): boolean {
    return this.open;
  }

  /** Match `MiniMap.HideMiniMap`/`ShowMiniMap`: OpenWindow hides, CloseWindow
   * shows (`uiminimap.py:455-466`). Server facts live under OpenWindow, so they
   * leave with the radar the way the native PositionInfo/ObserverCount do. */
  hideMiniMap(): void {
    this.visible = false;
    this.setOpen(false);
    this.radarCanvas.hidden = true;
    this.openWindow.hidden = true;
    this.closeWindow.hidden = false;
  }

  showMiniMap(): void {
    this.visible = true;
    this.radarCanvas.hidden = false;
    this.openWindow.hidden = false;
    this.closeWindow.hidden = true;
  }

  /** `ToggleMiniMap` (`interfacemodule.py:908-917`): with Shift the radar is
   *  shown or hidden, without it the atlas is toggled. */
  pressM(shift: boolean): void {
    if (!shift) this.toggleAtlas();
    else if (this.isShowMiniMap()) this.hideMiniMap();
    else this.showMiniMap();
  }

  isShowMiniMap(): boolean {
    return this.visible;
  }

  scaleUp(): number {
    this.setScale(nextMinimapScaleUp(this.scale));
    return this.scale;
  }

  scaleDown(): number {
    this.setScale(nextMinimapScaleDown(this.scale));
    return this.scale;
  }

  get minimapScale(): number {
    return this.scale;
  }

  /** The roster `SendNPCPosition` sends once at join
   * (`sectree_manager.cpp:1051-1090`). The server owns which NPCs its map
   * placed and where; the shipped `atlasLabelsFor` list is what this client
   * draws until it is told, and an empty roster is a real answer -- a map that
   * places no NPCs draws none. */
  setNpcMarkers(markers: readonly NpcMarker_[]): void {
    this.labels = markers.map((marker) => ({
      // `CHAR_TYPE_WARP` (3) and `CHAR_TYPE_GOTO` (9) are both a way out of
      // here; the atlas draws them the same and always has.
      kind: marker.kind === CHAR_TYPE_NPC ? "npc" : "warp",
      xMm: marker.xMm,
      yMm: marker.yMm,
      name: marker.name,
    }));
    if (this.open) this.paintAtlas();
  }

  /** Server state, not a client timer: each update replaces the duration with
   * a fresh duration and this client only interpolates the visible text. */
  setImprisonmentDuration(seconds: number): void {
    const safe = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
    // `update` runs every render frame. Do not restart a countdown merely
    // because the same server duration is still being displayed.
    if (safe === this.imprisonmentSeconds) return;
    this.imprisonmentSeconds = safe;
    this.imprisonmentUntilMs = safe > 0 ? performance.now() + safe * 1000 : 0;
  }

  updateObserverCount(observerCount: number): void {
    const safe = Math.max(0, Math.floor(Number.isFinite(observerCount) ? observerCount : 0));
    this.infoRoot.dataset.observerCount = String(safe);
  }

  private paintRadar(): void {
    const ctx = this.radarCtx;
    // `SetMiniMapSize(128, 128)` is independent of m_fScale in the native
    // renderer (`PythonMiniMap.cpp:508-512,1426-1430`). Zoom must therefore
    // change what fits in this disc, never resize the disc itself.
    const sizePx = RADAR_SIZE_PX * this.dpr;
    const pxPerMm = radarPixelsPerMm(sizePx, this.scale);

    ctx.save();
    ctx.clearRect(0, 0, sizePx, sizePx);
    ctx.beginPath();
    ctx.arc(sizePx / 2, sizePx / 2, sizePx / 2, 0, Math.PI * 2);
    ctx.clip();

    this.paintGround(ctx, sizePx / 2, sizePx / 2, pxPerMm);

    for (const entity of this.last.entities) {
      const { dx, dy } = radarOffset(entity.xMm, entity.yMm, this.last.xMm, this.last.yMm, pxPerMm);
      this.dot(ctx, sizePx / 2 + dx, sizePx / 2 + dy, 3.2 * this.dpr, dotColour(entity.kind));
    }

    for (const target of this.questTargets.values()) {
      const marker = radarQuestTargetMarker(target.xMm, target.yMm, this.last.xMm, this.last.yMm, pxPerMm, sizePx / 2);
      if (marker.inside) this.targetMarker(ctx, marker.x, marker.y, 5 * this.dpr);
      else this.targetArrow(ctx, marker.x, marker.y, marker.angle, 7 * this.dpr);
    }

    this.drawPlayer(ctx, sizePx / 2, sizePx / 2, 5 * this.dpr);
    ctx.restore();

    ctx.strokeStyle = "rgba(233, 238, 245, 0.35)";
    ctx.lineWidth = 1.5 * this.dpr;
    ctx.beginPath();
    ctx.arc(sizePx / 2, sizePx / 2, sizePx / 2 - ctx.lineWidth / 2, 0, Math.PI * 2);
    ctx.stroke();
  }

  private paintGround(
    ctx: CanvasRenderingContext2D,
    centreX: number,
    centreY: number,
    pxPerMm: number,
  ): void {
    if (this.overview === null) {
      ctx.fillStyle = "#26301f";
      ctx.fillRect(0, 0, centreX * 2, centreY * 2);
      return;
    }
    const rect = groundDrawRect(
      this.last.xMm,
      this.last.yMm,
      pxPerMm,
      this.overview.width,
      this.overview.height,
      centreX,
      centreY,
      this.map,
    );
    ctx.drawImage(this.overview, rect.x, rect.y, rect.width, rect.height);
  }

  private paintAtlas(): void {
    const ctx = this.atlasCtx;
    const width = this.atlasCanvas.width;
    const height = this.atlasCanvas.height;
    ctx.clearRect(0, 0, width, height);

    if (this.overview === null) {
      ctx.fillStyle = "#26301f";
      ctx.fillRect(0, 0, width, height);
    } else {
      ctx.drawImage(this.overview, 0, 0, width, height);
    }

    for (const label of this.labels) {
      const { x, y } = fractionToPixel(worldToFraction(label.xMm, label.yMm, this.map), width, height);
      const colour = label.kind === "warp" ? "#f2c14e" : "#9fd6e8";
      this.dot(ctx, x, y, 3, colour);
      ctx.font = "600 10px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
      ctx.fillText(label.name, x + 1, y - 6 + 1);
      ctx.fillStyle = colour;
      ctx.fillText(label.name, x, y - 6);
    }

    for (const entity of this.last.entities) {
      const { x, y } = fractionToPixel(worldToFraction(entity.xMm, entity.yMm, this.map), width, height);
      this.dot(ctx, x, y, 2.5, dotColour(entity.kind));
    }

    for (const target of this.questTargets.values()) {
      const { x, y } = fractionToPixel(worldToFraction(target.xMm, target.yMm, this.map), width, height);
      this.targetMarker(ctx, x, y, 4);
      ctx.font = "600 10px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
      ctx.fillText(target.description, x + 1, y - 7 + 1);
      ctx.fillStyle = "#ffd27a";
      ctx.fillText(target.description, x, y - 7);
    }

    const playerPx = fractionToPixel(
      worldToFraction(this.last.xMm, this.last.yMm, this.map),
      width,
      height,
    );
    this.drawPlayer(ctx, playerPx.x, playerPx.y, 6);

    // After the player mark, the flag alone and no outline: the rectangle
    // `RenderBar2d` would draw is commented out (`PythonMiniMap.cpp:1036-1050`).
    const flag = this.guildAreaFlag;
    if (!flag.complete || flag.naturalWidth === 0) return;
    for (const area of this.guildAreas) {
      const at = guildAreaFlagPosition(area, this.map, width, height, flag.naturalWidth, flag.naturalHeight);
      ctx.drawImage(flag, at.x, at.y);
    }
  }

  private setScale(scale: number): void {
    this.scale = scale;
    this.paintRadar();
  }

  private showAtlasInfo(event: PointerEvent): void {
    const bounds = this.atlasCanvas.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    const u = (event.clientX - bounds.left) / bounds.width;
    const v = (event.clientY - bounds.top) / bounds.height;
    const info = infoAtFraction(u, v, this.map, this.labels);
    const plot = info.label === null ? guildAreaInfoAt(u, v, this.map, this.guildOwners, this.guildNames) : null;
    const named = info.label !== null
      ? { name: info.label.name, xMm: info.xMm, yMm: info.yMm }
      : plot;
    if (named === null) {
      this.atlasTooltip.hidden = true;
    } else {
      // `MapTextToolTip.SetTooltipPosition` keeps the native label beside the
      // pointer (`uiminimap.py:28-36,136-153`). The browser's atlas has the
      // same person-recognisable affordance; the fixed position also keeps the
      // label from changing the map's layout while it is being inspected.
      // `"%s(%d, %d)"` (`uiminimap.py:148`).
      this.atlasTooltip.textContent = `${named.name}(${Math.round(named.xMm / 10)}, ${Math.round(named.yMm / 10)})`;
      if (plot !== null && plot.guildId !== 0) {
        const rgba = this.guildEmblems.get(plot.guildId);
        if (rgba === undefined) this.wantEmblems([plot.guildId]);
        else this.atlasTooltip.prepend(markNode(rgba, this.atlasTooltip.ownerDocument));
      }
      this.atlasTooltip.style.left = `${event.clientX + 10}px`;
      this.atlasTooltip.style.top = `${event.clientY + 10}px`;
      this.atlasTooltip.hidden = false;
    }
  }

  private paintServerInfo(): void {
    const observerCount = Number(this.infoRoot.dataset.observerCount ?? 0);
    const parts = [LEGACY_MINIMAP_SERVER_INFO, legacyMinimapPositionLabel(this.last.xMm, this.last.yMm)];
    if (observerCount > 0) parts.push(legacyMinimapObserverLabel(observerCount));
    const remaining = this.imprisonmentUntilMs > 0
      ? Math.max(0, (this.imprisonmentUntilMs - performance.now()) / 1000)
      : 0;
    if (remaining > 0) parts.push(`Imprisoned: ${formatDuration(remaining)}`);
    this.infoRoot.textContent = parts.join(" · ");
  }

  /** The player's own mark: a triangle rather than a dot, because facing is
   *  half of "where am I" and a dot cannot show it. */
  private drawPlayer(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(headingToScreenAngle(this.last.heading));
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(-radius * 0.7, radius * 0.7);
    ctx.lineTo(-radius * 0.35, 0);
    ctx.lineTo(-radius * 0.7, -radius * 0.7);
    ctx.closePath();
    ctx.fillStyle = ENTITY_COLOURS.player;
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(0, 0, 0, 0.6)";
    ctx.stroke();
    ctx.restore();
  }

  private dot(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, colour: string): void {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = colour;
    ctx.fill();
  }

  /** A diamond keeps a quest waypoint distinct from entity dots at a glance. */
  private targetMarker(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(Math.PI / 4);
    ctx.fillStyle = "#ffd27a";
    ctx.fillRect(-radius * 0.7, -radius * 0.7, radius * 1.4, radius * 1.4);
    ctx.restore();
  }

  private targetArrow(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number, radius: number): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillStyle = "#ffd27a";
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(-radius * 0.65, radius * 0.55);
    ctx.lineTo(-radius * 0.3, 0);
    ctx.lineTo(-radius * 0.65, -radius * 0.55);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
}

function dotColour(kind: number): string {
  return ENTITY_DOT_COLOUR[kind] ?? ENTITY_COLOURS.other;
}

function controlButton(text: string, label: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = text;
  button.title = label;
  button.setAttribute("aria-label", label);
  return button;
}

function placeControl(
  button: HTMLButtonElement,
  layout: { readonly x: number; readonly y: number; readonly w: number; readonly h: number },
  className: string,
): void {
  button.classList.add("minimap-control", className);
  button.style.left = `${layout.x}px`;
  button.style.top = `${layout.y}px`;
  button.style.width = `${layout.w}px`;
  button.style.height = `${layout.h}px`;
}

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.minimap {
  /* Fixed -- see .bag in style.css: #overlay is a zero-width flex column, so
     absolute children resolve against it and land in the wrong corner. */
  position: fixed;
  top: 0;
  right: 0;
  width: ${MINIMAP_WINDOW_WIDTH}px;
  height: ${MINIMAP_WINDOW_HEIGHT}px;
  z-index: 20;
  pointer-events: none;
  font-family: Tahoma, "MS Sans Serif", "Segoe UI", sans-serif;
  color: #e8dcc0;
  image-rendering: pixelated;
}

.minimap-open {
  position: absolute;
  inset: 0;
  /* OpenWindowBGI (uiscript/minimap.py:32): the 136x137 frame itself, over
     the fill so the dial inside the ring is not transparent. */
  background: url("${MINIMAP_ART}/minimap.png") left top no-repeat,
    ${MINIMAP_BOARD_FILL};
  box-sizing: border-box;
  pointer-events: none;
}

.minimap-close {
  position: absolute;
  top: 0;
  right: 0;
  width: ${MINIMAP_LAYOUT.closeWindow.w}px;
  height: ${MINIMAP_LAYOUT.closeWindow.h}px;
  pointer-events: none;
}

.minimap-radar {
  position: absolute;
  left: ${MINIMAP_RADAR_OFFSET_X}px;
  top: ${MINIMAP_RADAR_OFFSET_Y}px;
  width: ${RADAR_SIZE_PX}px;
  height: ${RADAR_SIZE_PX}px;
  border-radius: 50%;
  border: 0;
  background: #26301f;
  box-shadow: none;
  pointer-events: none;
}

.minimap-info {
  position: absolute;
  left: 0;
  right: 0;
  top: ${MINIMAP_LAYOUT.infoY}px;
  max-width: none;
  padding: 0;
  border-radius: 0;
  color: #e8dcc0;
  background: transparent;
  font: 11px Tahoma, "MS Sans Serif", "Segoe UI", sans-serif;
  text-align: center;
  text-shadow: 0 0 1px #000, 1px 1px 0 #000;
  pointer-events: none;
  white-space: nowrap;
}

.minimap-controls {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.minimap-control {
  position: absolute;
  padding: 0;
  margin: 0;
  border: 0;
  border-radius: 0;
  /* The glyph stays for a reader; the button is its sprite. */
  color: transparent;
  background: no-repeat left top;
  cursor: pointer;
  pointer-events: auto;
  font-size: 0;
  image-rendering: pixelated;
}
/* Each control in the three faces its layout names: ScaleUpButton
   (uiscript/minimap.py:53-55), ScaleDownButton (:65-67), MiniMapHideButton
   (:77-79), AtlasShowButton (:89-91) and MiniMapShowButton, whose three
   states are one file (:157-159). */
${[
  ["scale-up", "minimap_scaleup"],
  ["scale-down", "minimap_scaledown"],
  ["hide", "minimap_close"],
  ["atlas-open", "atlas_open"],
].map(([suffix, stem]) =>
  `.minimap-${suffix} { background-image: url("${MINIMAP_ART}/${stem}_default.png"); }\n`
  + `.minimap-${suffix}:hover { background-image: url("${MINIMAP_ART}/${stem}_over.png"); }\n`
  + `.minimap-${suffix}:active { background-image: url("${MINIMAP_ART}/${stem}_down.png"); }`,
).join("\n")}
.minimap-show { background-image: url("${MINIMAP_ART}/minimap_open_default.png"); }

.atlas-backdrop {
  /* Anchored left of the 136px MiniMap: native AtlasWindow uses
     GetScreenWidth() - 136 - 256 - 10 (uiminimap.py:171-182). Never a
     full-viewport modal; pointer-events none so empty margin cannot steal
     clicks from the world. Below every window that SetTops on open: Show
     never raises it (uiminimap.py:168-181) and it is built before them
     (interfacemodule.py:179, :213), so an opened shop (uishop.py:207) covers
     it. */
  position: fixed;
  top: 0;
  right: ${MINIMAP_WINDOW_WIDTH + 10}px;
  z-index: 39;
  pointer-events: none;
  /* The world is drawn whole and the interface over it, one back buffer
     (PythonApplication.cpp:705-717); below 1 the board is no opaque occluder
     Chromium culls the world canvas under, and 0.999 moves no 8-bit pixel. */
  opacity: 0.999;
}

.atlas-panel {
  pointer-events: auto;
  padding: 6px;
  border-radius: 0;
  border: 1px solid #3a2f22;
  background: #1a1510;
  box-shadow: none;
  font-family: Tahoma, "MS Sans Serif", "Segoe UI", sans-serif;
  color: #e8dcc0;
}

.atlas-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
}

.atlas-close {
  width: 16px;
  height: 14px;
  padding: 0;
  border: 0;
  border-radius: 0;
  color: transparent;
  background: url("/ui/textures/public/close_button_01.png") center / contain no-repeat;
  cursor: pointer;
  image-rendering: pixelated;
}
.atlas-close:hover { background-image: url("/ui/textures/public/close_button_02.png"); }
.atlas-close:active { background-image: url("/ui/textures/public/close_button_03.png"); }

.atlas-canvas {
  /* width and aspect-ratio are set inline per instance, from the map's own
     extent -- a1 is 4:5 and a3 is square, and one rule cannot be both. Not
     backtick-quoted above: this whole block is a template literal. */
  display: block;
  height: auto;
  border-radius: 0;
}

.atlas-tooltip {
  position: fixed;
  z-index: 62;
  padding: 3px 7px;
  border-radius: 0;
  color: #e8dcc0;
  background: #1a1510;
  border: 1px solid #3a2f22;
  box-shadow: none;
  font: 12px Tahoma, "MS Sans Serif", sans-serif;
  white-space: nowrap;
  pointer-events: none;
}
`;
  document.head.append(style);
}
